import React from 'react';
import '../styles/PaginaVendaSaas.css';

const adminEndpoints = [
  ['GET', '/api/erp/v1/integrations/tokens', 'Lista os tokens da empresa. Exige sessão de owner ou manager.'],
  ['POST', '/api/erp/v1/integrations/tokens', 'Cria um token M2M. O valor completo é exibido somente nesta resposta.'],
  ['DELETE', '/api/erp/v1/integrations/tokens/:tokenId', 'Revoga um token da empresa.'],
];

const integrationEndpoints = [
  ['GET', '/api/erp/v1/integrations/v1/account', 'Confirma a empresa e os escopos vinculados ao token.'],
  ['GET', '/api/erp/v1/integrations/v1/health', 'Health check autenticado e somente leitura.'],
  ['GET', '/api/erp/v1/integrations/v1/sync/state', 'Consulta cursores e estado da sincronização.'],
  ['GET', '/api/erp/v1/integrations/v1/catalog/snapshot', 'Obtém o snapshot de pedidos, produtos e estoque.'],
  ['GET', '/api/erp/v1/integrations/v1/events', 'Lê eventos por cursor, com paginação e long-polling limitado.'],
  ['POST', '/api/erp/v1/integrations/v1/sync/cursor/ack', 'Confirma o cursor somente após aplicar os eventos.'],
  ['POST', '/api/erp/v1/integrations/v1/sync/reconcile', 'Solicita reconciliação após perda de cursor ou divergência.'],
  ['POST', '/api/erp/v1/integrations/v1/sync/commands', 'Publica somente comandos explicitamente aceitos pelo contrato.'],
  ['GET', '/api/erp/v1/integrations/v1/sync/commands/:commandId', 'Consulta o resultado de um comando aceito.'],
];

const scopes = [
  ['account:read', 'Conta vinculada e health check.'],
  ['catalog:read', 'Snapshot de pedidos, produtos e estoque.'],
  ['events:read', 'Estado, eventos, cursores, ack, reconciliação e status de comandos.'],
  ['sync:write', 'Comandos publicados pelo diário central do Nivora.'],
];

const events = [
  'snapshot inicial',
  'eventos por cursor',
  'acknowledgement',
  'reconciliação',
  'status de comando',
];

function EndpointList({ items }) {
  return (
    <div className="api-docs-endpoints">
      {items.map(([method, path, text]) => (
        <article key={`${method}-${path}`}>
          <strong>{method}</strong>
          <code>{path}</code>
          <span>{text}</span>
        </article>
      ))}
    </div>
  );
}

export default function DocumentacaoAPI() {
  const productLogo = `${process.env.PUBLIC_URL || ''}/assets/nivora-logo.png`;
  return (
    <div className="sales-page api-docs-page">
      <header className="sales-topbar">
        <a className="sales-brand" href="/" aria-label="Nivora ERP">
          <span><img src={productLogo} alt="" aria-hidden="true" /></span>
          Nivora ERP
        </a>
        <nav className="sales-nav" aria-label="Navegação da documentação">
          <a href="/">Página de venda</a>
          <a href="#autenticacao">Autenticação</a>
          <a href="#endpoints">Endpoints</a>
          <a href="#sincronizacao">Sincronização</a>
          <a href="/login#/criar-conta" className="sales-nav-cta">Criar conta</a>
        </nav>
      </header>

      <main>
        <section className="api-docs-hero">
          <span className="sales-section-label">Contrato implementado</span>
          <h1>API de integração do Nivora ERP</h1>
          <p>
            Integre e-commerces, CRMs, aplicativos e soluções parceiras por uma API REST
            autenticada, limitada por escopos e isolada por empresa.
          </p>
          <div className="sales-actions">
            <a className="sales-button sales-button-primary" href="#endpoints">
              <i className="fas fa-terminal" aria-hidden="true"></i>
              Ver endpoints
            </a>
            <a className="sales-button sales-button-secondary" href="#sincronizacao">
              <i className="fas fa-arrows-rotate" aria-hidden="true"></i>
              Ver fluxo de sincronização
            </a>
          </div>
          <p className="api-docs-base-url">
            Base de produção: <code>https://nivoraerp.com.br/api/erp/v1/integrations</code>
          </p>
        </section>

        <section className="sales-section api-docs-grid" id="autenticacao">
          <article>
            <i className="fas fa-key" aria-hidden="true"></i>
            <h2>Token M2M</h2>
            <p>
              Um owner ou manager cria o token pelo painel autenticado. O segredo completo
              aparece uma única vez e nunca deve ser enviado ao navegador, URL, log ou repositório.
            </p>
            <pre>{`POST /api/erp/v1/integrations/tokens
Authorization: Bearer <sessao-do-owner>
Content-Type: application/json

{ "name": "Conector parceiro", "scopes": ["account:read", "catalog:read"] }`}</pre>
          </article>
          <article>
            <i className="fas fa-shield-halved" aria-hidden="true"></i>
            <h2>Escopos</h2>
            <p>O token determina o que o parceiro pode consultar ou publicar.</p>
            <pre>{scopes.map(([scope, description]) => `${scope} — ${description}`).join('\n')}</pre>
          </article>
          <article>
            <i className="fas fa-building-shield" aria-hidden="true"></i>
            <h2>Empresa do token</h2>
            <p>
              A empresa é resolvida exclusivamente pelo token. O integrador não envia
              <code>tenantId</code>, <code>empresaId</code> ou seletor equivalente.
            </p>
            <pre>{`Authorization: Bearer nivora_it_<token>
Accept: application/json

// Não envie tenantId no corpo, query ou header.`}</pre>
          </article>
        </section>

        <section className="sales-section" id="endpoints">
          <div className="sales-section-heading">
            <span className="sales-section-label">Administração de credenciais</span>
            <h2>Gerencie tokens sem expor segredos.</h2>
            <p>Estas rotas usam a sessão do owner ou manager, não o token M2M.</p>
          </div>
          <EndpointList items={adminEndpoints} />
        </section>

        <section className="sales-section" aria-labelledby="integration-endpoints-title">
          <div className="sales-section-heading">
            <span className="sales-section-label">REST M2M</span>
            <h2 id="integration-endpoints-title">Superfície de integração disponível.</h2>
            <p>Todos os caminhos abaixo são relativos à base de produção informada acima.</p>
          </div>
          <EndpointList items={integrationEndpoints} />
        </section>

        <section className="sales-band" id="sincronizacao">
          <div className="sales-band-copy">
            <span className="sales-section-label">Fluxo recomendado</span>
            <h2>Snapshot inicial, eventos e cursor confirmado.</h2>
            <p>
              Faça um snapshot, persista o cursor, aplique cada evento de forma idempotente,
              confirme o cursor apenas depois da aplicação e reconcilie após quedas ou divergências.
              O Nivora não envia dados de outra empresa para o token.
            </p>
          </div>
          <div className="api-docs-events">
            {events.map((event) => <span key={event}>{event}</span>)}
          </div>
        </section>

        <section className="sales-section api-docs-sample">
          <div>
            <span className="sales-section-label">Exemplo de leitura</span>
            <h2>Verifique a conta antes de sincronizar.</h2>
            <p>
              Em caso de <code>401</code>, renove ou revogue a credencial. Em <code>403</code>,
              solicite o escopo necessário. Em <code>429</code>, respeite <code>Retry-After</code>.
            </p>
          </div>
          <pre>{`curl -sS \
  -H "Authorization: Bearer nivora_it_<token>" \
  -H "Accept: application/json" \
  https://nivoraerp.com.br/api/erp/v1/integrations/v1/account

{ "ok": true, "data": {
    "tenant": { "id": "...", "slug": "...", "name": "..." },
    "token": { "id": "...", "scopes": ["account:read"] }
  }
}`}</pre>
        </section>

        <section className="sales-section api-docs-security-note">
          <span className="sales-section-label">Segurança e limites</span>
          <h2>O contrato rejeita escopo cruzado e payloads indevidos.</h2>
          <p>
            Use HTTPS, mantenha o token em cofre, aplique backoff com jitter em falhas transitórias,
            não repita uma escrita sem a mesma chave de idempotência e guarde o <code>requestId</code>
            para suporte. Rotas de provedores de pagamento e webhooks específicos não fazem parte
            desta API genérica; use a documentação da integração correspondente.
          </p>
        </section>

        <footer className="sales-footer">
          <strong>Nivora ERP</strong>
          <span>Documentação pública para parceiros e soluções integradas.</span>
          <a href="/">Voltar para página de venda</a>
        </footer>
      </main>
    </div>
  );
}
